export const buildDepartmentTreeDisplayName = (code, name) => {
  const strCode = code ? String(code) : "";
  let codeStr = "";
  if (strCode) {
    codeStr = strCode.startsWith("#") ? strCode : `#${strCode}`;
  }
  const label = (name || "").trim();
  if (codeStr && label) return `${codeStr} - ${label}`;
  return codeStr || label || "";
};

export const mapDepartmentToTreeNode = (department, children = []) => ({
  id: department.id,
  type: "department",
  name: buildDepartmentTreeDisplayName(department.code, department.name),
  code: department.code || "",
  status: department.status || "draft",
  expanded: false,
  children,
});

export const mapFlatDepartmentRowsToTreeData = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const roots = [];
  const stack = [];

  rows.forEach((row) => {
    if (!row) return;
    const node = mapDepartmentToTreeNode(row, []);
    const level = row.level ?? 0;

    while (stack.length > 0 && stack.at(-1).level >= level) {
      stack.pop();
    }

    if (stack.length === 0) {
      roots.push(node);
    } else {
      stack.at(-1).node.children.push(node);
    }

    stack.push({ node, level });
  });

  return roots;
};

const buildDepartmentHierarchyRoots = (departments) => {
  const byId = new Map();
  departments.forEach((department) => {
    byId.set(department.id, { ...department, children: [] });
  });

  const roots = [];
  departments.forEach((department) => {
    const parentId =
      department.parent_department_id ?? department.parent_id ?? department.parent ?? null;
    const node = byId.get(department.id);
    if (parentId != null && byId.has(parentId)) {
      byId.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
};

const mapLevelBasedDepartmentsToRows = (departments) =>
  departments.map((department, index) => {
    const next = departments[index + 1];
    const hasChildren = next && (next.level ?? 0) > (department.level ?? 0);
    return mapDepartmentToTableRow(department, department.level ?? 0, hasChildren);
  });

const hasParentReference = (department) =>
  department.parent_department_id !== undefined ||
  department.parent_id !== undefined ||
  department.parent !== undefined;

export const mapApiDepartmentsToTreeData = (departments) => {
  if (!Array.isArray(departments) || departments.length === 0) {
    return [];
  }

  // The parent relationship is the source of truth; row order + `level` can misattach parents.
  const useLevels =
    !departments.some(hasParentReference) &&
    departments.some((department) => department.level != null);
  if (useLevels) {
    return mapFlatDepartmentRowsToTreeData(mapLevelBasedDepartmentsToRows(departments));
  }

  const roots = buildDepartmentHierarchyRoots(departments);

  // Sibling order comes from the API (manual `sequence`), so it must not be re-sorted here.
  const toTreeNodes = (nodes) =>
    nodes.map((node) => mapDepartmentToTreeNode(node, toTreeNodes(node.children || [])));

  return toTreeNodes(roots);
};

export const mapDepartmentToTableRow = (department, level = 0, hasChildren = false) => {
  const status = department.status || "draft";
  const isRetired = status.toLowerCase() === "retired";
  const rawCount =
    department.direct_positions_count ??
    department.positions_count ??
    department.position_count ??
    null;
  const rawSubtreeCount = department.positions_count_subtree ?? null;
  return {
    id: department.id,
    code: department.code || "",
    name: department.name || "",
    status,
    level: department.level ?? level,
    expansionState: hasChildren ? "Expanded" : "No Expand",
    positionsCount: isRetired ? 0 : rawCount,
    positionsCountSubtree: isRetired ? 0 : rawSubtreeCount,
  };
};

export const mapApiDepartmentsToTableRows = (departments) => {
  if (!Array.isArray(departments) || departments.length === 0) {
    return [];
  }

  const hasLevel = departments.some((department) => department.level != null);
  if (hasLevel) {
    return mapLevelBasedDepartmentsToRows(departments);
  }

  const roots = buildDepartmentHierarchyRoots(departments);

  const rows = [];
  const walk = (nodes, level) => {
    nodes.forEach((node) => {
      const hasChildren = node.children?.length > 0;
      rows.push(mapDepartmentToTableRow(node, level, hasChildren));
      if (hasChildren) {
        walk(node.children, level + 1);
      }
    });
  };

  // Sibling order comes from the API (manual `sequence`), so it must not be re-sorted here.
  walk(roots, 0);
  return rows;
};

const normalizeReadinessPercent = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return num <= 1 ? Math.round(num * 100) : Math.round(num);
};

export const DEFAULT_DEPARTMENT_READINESS = { percent: 0, changePercent: null };

export const mapDepartmentDetailReadiness = (department = {}) => {
  const percent = normalizeReadinessPercent(
    department.avg_readiness ?? department.average_readiness ?? department.current_readiness
  );
  if (percent == null) return { ...DEFAULT_DEPARTMENT_READINESS };

  const lastMonth = normalizeReadinessPercent(
    department.last_month_readiness ??
      department.last_month_avg_readiness ??
      department.last_month_avg_employee_readiness
  );

  return {
    percent,
    changePercent: lastMonth == null ? null : percent - lastMonth,
  };
};

export const normalizePositionCode = (position = {}) => {
  const raw = position.code_str?.split?.(" ")?.[0] || position.code || "";
  return String(raw).replace(/^#+/, "");
};

const formatEmployeeAvatarUrl = (avatar) => {
  if (!avatar) return null;
  const value = String(avatar);
  return value.startsWith("http") ? value : `https://d2ox6kgfo0vgc9.cloudfront.net/${value}`;
};

export const resolvePositionOccupant = (position = {}) => {
  const occupant = position.assigned_primary || position.occupied_by || null;

  if (!occupant) return null;

  const name =
    occupant.assigned_employee_name?.trim() ||
    [occupant.first_name, occupant.last_name].filter(Boolean).join(" ").trim() ||
    occupant.name?.trim() ||
    "";

  if (!name) return null;

  return {
    id: occupant.assigned_employee_id ?? occupant.id ?? null,
    name,
    title: occupant.title || occupant.position_title || occupant.assigned_employee_title || "",
    avatar: formatEmployeeAvatarUrl(
      occupant.assigned_employee_avatar || occupant.avatar || occupant.picture
    ),
  };
};

export const mapApiPositionToDepartmentCardRow = (position = {}) => ({
  id: position.id,
  departmentId: position.department_id ?? null,
  name: position.name || "",
  code: normalizePositionCode(position),
  state: String(position.status || "").toLowerCase() === "active" ? "ACTIVE" : "DISABLED",
  status: String(position.status || "draft").toUpperCase(),
  menuStatus: String(position.status || "draft").toLowerCase(),
  assigned_primary: position.assigned_primary ?? null,
  archived: Boolean(position.archived),
  occupiedBy: resolvePositionOccupant(position),
});

export const mapDepartmentDetailToCardProps = (department = {}) => {
  const code = String(department.code || "").replace(/^#/, "");

  return {
    name: department.name || "",
    code,
    status: String(department.status || "draft").toLowerCase(),
    readiness: mapDepartmentDetailReadiness(department),
    chatterUnreadCount:
      department.chatter_unread_count ??
      department.unread_chatter_count ??
      department.unread_chatter ??
      0,
    positionsCount:
      department.positions_count ??
      department.active_positions_count ??
      department.position_count ??
      null,
  };
};
